/** Modelos partilhados no workspace: as regras, sem React nem rede.
 *
 *  A regra da casa era "o IFC nunca sai da máquina de quem o abre". Com
 *  o módulo "modelos" há uma excepção explícita e opcional por ficheiro:
 *  quem carrega escolhe "Partilhar no workspace". Quem não escolhe
 *  continua como sempre. Ver docs/MODELOS_PARTILHADOS.md.
 *
 *  Um ficheiro é identificado pelo SHA-256 dos bytes do IFC (o mesmo que
 *  o viewer já calcula ao abrir). O mesmo nome com outro SHA-256 é uma
 *  revisão nova; as anteriores ficam, numeradas.
 */

export interface EstadoModelosServidor {
  ativo: boolean
  motivo: string | null
  modo: string | null
  bloco_bytes: number
  /** Limite por ficheiro, comprimido. */
  max_ficheiro_bytes: number
  /** Limite por ficheiro, antes de comprimir. */
  max_original_bytes: number
  /** Espaço do workspace, comprimido. */
  quota_bytes: number
  usado_bytes: number
  /** 'workspace': `usado_bytes` é o total (administração); 'minha': só o
   *  que esta pessoa carregou (os outros não veem o resto). */
  ocupacao?: 'workspace' | 'minha'
}

/** A linha do espaço ocupado, conforme quem a vê. */
export function textoDaOcupacao(e: Pick<EstadoModelosServidor, 'usado_bytes' | 'quota_bytes' | 'ocupacao'>, formatar: (n: number) => string): string {
  if (e.ocupacao === 'minha' && e.usado_bytes === 0) return 'Ainda não guardou ficheiros no servidor'
  return e.ocupacao === 'minha'
    ? `Os seus ficheiros ocupam ${formatar(e.usado_bytes)}`
    : `Ocupado no workspace: ${formatar(e.usado_bytes)} de ${formatar(e.quota_bytes)}`
}

export interface ModeloPartilhado {
  id: string
  nome: string
  revisao: number
  sha256: string
  /** Do IFC por comprimir. */
  tamanho: number
  /** O que ocupa no servidor. */
  tamanho_comprimido: number
  schema_ifc: string | null
  carregado_por_nome: string | null
  carregado_por_email: string | null
  carregado_em: string
  /** É a revisão mais recente do seu nome. */
  ultima: boolean
  pode_apagar: boolean
  /** A partilha do nome (a lista de acessos vale para todas as
   *  revisões). Ver docs/PARTILHA.md. */
  partilha_id?: string | null
  pode_gerir?: boolean
  visivel_para?: string | null
}

export interface PedidoDeEnvio {
  id: string
  nome: string
  revisao: number
  bloco_bytes: number
  numero_de_blocos: number
  em_falta: number[]
}

export interface PedidoDeDescarga {
  id: string
  nome: string
  revisao: number
  sha256: string
  tamanho: number
  tamanho_comprimido: number
  bloco_bytes: number
  numero_de_blocos: number
}

export interface GrupoDeModelo {
  chave: string
  /** O nome da revisão mais recente. */
  nome: string
  /** Da mais recente para a mais antiga. */
  revisoes: ModeloPartilhado[]
  /** Soma do espaço ocupado por todas as revisões. */
  ocupado: number
}

/** A mesma regra do servidor (regras.nome_limpo + lower): sem caminho,
 *  espaços normalizados, sem diferença entre maiúsculas e minúsculas. */
export function chaveDoNome(nome: string): string {
  const base = nome.split(/[\\/]/).pop() ?? ''
  return base.normalize('NFC').split(/\s+/).filter(Boolean).join(' ').toLowerCase()
}

/** Tamanho legível com vírgula decimal (português de Portugal). */
export function formatarBytes(n: number): string {
  const unidades = ['bytes', 'kB', 'MB', 'GB', 'TB']
  let v = n
  let i = 0
  while (v >= 1024 && i < unidades.length - 1) {
    v /= 1024
    i++
  }
  if (i === 0) return `${Math.round(v)} bytes`
  const texto = v.toFixed(1).replace('.', ',').replace(/,0$/, '')
  return `${texto} ${unidades[i]}`
}

export function agruparPorNome(
  lista: readonly ModeloPartilhado[],
  ordem: 'nome' | 'espaco' = 'nome',
): GrupoDeModelo[] {
  const grupos = new Map<string, ModeloPartilhado[]>()
  for (const m of lista) {
    const k = chaveDoNome(m.nome)
    const g = grupos.get(k)
    if (g) g.push(m)
    else grupos.set(k, [m])
  }
  const saida: GrupoDeModelo[] = [...grupos.entries()].map(([chave, revs]) => {
    const revisoes = [...revs].sort((a, b) => b.revisao - a.revisao)
    return {
      chave,
      nome: revisoes[0].nome,
      revisoes,
      ocupado: revisoes.reduce((s, r) => s + r.tamanho_comprimido, 0),
    }
  })
  saida.sort((a, b) =>
    ordem === 'espaco'
      ? b.ocupado - a.ocupado || a.chave.localeCompare(b.chave)
      : a.chave.localeCompare(b.chave),
  )
  return saida
}

export function encontrarPorSha(
  lista: readonly ModeloPartilhado[] | null | undefined,
  sha256: string,
): ModeloPartilhado | null {
  if (!lista) return null
  const s = sha256.toLowerCase()
  return lista.find((m) => m.sha256.toLowerCase() === s) ?? null
}

/** A revisão mais recente do mesmo nome, se for outra que não esta.
 *  É o que alimenta o aviso "há uma revisão nova". */
export function revisaoMaisRecente(
  lista: readonly ModeloPartilhado[] | null | undefined,
  sha256: string,
): ModeloPartilhado | null {
  const esta = encontrarPorSha(lista, sha256)
  if (!esta || !lista) return null
  const k = chaveDoNome(esta.nome)
  let maior: ModeloPartilhado | null = null
  for (const m of lista) {
    if (chaveDoNome(m.nome) !== k) continue
    if (!maior || m.revisao > maior.revisao) maior = m
  }
  return maior && maior.revisao > esta.revisao ? maior : null
}

/** Revisões que apareceram entre duas leituras da lista e que são mais
 *  recentes do que um modelo que eu tenho aberto. Cada uma avisa-se uma
 *  vez: quem chama guarda a lista anterior. Na primeira leitura
 *  (`anterior` nulo) não se avisa nada: não é novidade, é o arranque. */
export function revisoesNovasParaAvisar(
  anterior: readonly ModeloPartilhado[] | null,
  actual: readonly ModeloPartilhado[],
  shasAbertos: readonly string[],
): ModeloPartilhado[] {
  if (!anterior) return []
  const jaHavia = new Set(anterior.map((m) => m.id))
  const saida: ModeloPartilhado[] = []
  for (const sha of shasAbertos) {
    const nova = revisaoMaisRecente(actual, sha)
    if (nova && !jaHavia.has(nova.id) && !saida.some((m) => m.id === nova.id)) {
      saida.push(nova)
    }
  }
  return saida
}

/** Os modelos que outros participantes da sala têm abertos, eu não, e
 *  que estão partilhados no workspace: são os que se podem oferecer com
 *  um botão "Abrir". Os que não estão partilhados ficam de fora: a sala
 *  não envia ficheiros. */
export function modelosParaAbrirDaSala(
  modelosDosOutros: readonly { sha256: string }[],
  meusShas: readonly string[],
  lista: readonly ModeloPartilhado[] | null | undefined,
): ModeloPartilhado[] {
  const meus = new Set(meusShas.map((s) => s.toLowerCase()))
  const saida: ModeloPartilhado[] = []
  for (const m of modelosDosOutros) {
    const s = m.sha256.toLowerCase()
    if (meus.has(s)) continue
    const partilhado = encontrarPorSha(lista, s)
    if (partilhado && !saida.some((x) => x.id === partilhado.id)) saida.push(partilhado)
  }
  return saida
}

/** Fracção ocupada, entre 0 e 1 (para a barra de espaço). */
export function fraccaoOcupada(usado: number, quota: number): number {
  if (quota <= 0) return 1
  return Math.min(1, Math.max(0, usado / quota))
}

/** Numa sessão leve, os modelos partilhados vão por referência (o
 *  SHA-256) em vez de os bytes irem dentro do ficheiro. Devolve os
 *  hashes cujos bytes NÃO se embutem. Numa sessão normal, nenhum. */
export function shasPorReferencia(
  modelos: readonly { sha256: string; sourceFormat?: 'ifc' | 'frag' }[],
  lista: readonly ModeloPartilhado[] | null | undefined,
  leve: boolean,
): Set<string> {
  const saida = new Set<string>()
  if (!leve) return saida
  for (const m of modelos) {
    if (m.sourceFormat === 'frag') continue
    if (encontrarPorSha(lista, m.sha256)) saida.add(m.sha256.toLowerCase())
  }
  return saida
}

/** O que se diz a quem vai partilhar, antes de o ficheiro sair da
 *  máquina. É a confirmação explícita que a regra nova exige. */
export function textoDeConfirmacaoDaRevisao(
  nome: string,
  tamanho: number,
  visivelPara: string | null | undefined,
): string {
  return (
    `${textoDeConfirmacaoDaPartilha(nome, tamanho)}\n\n`
    + `É uma revisão nova de um modelo já partilhado. ${visivelPara ?? ''}`.trim()
  )
}

export function textoDeConfirmacaoDaPartilha(nome: string, tamanho: number): string {
  return (
    `Partilhar "${nome}" (${formatarBytes(tamanho)})?\n\n`
    + 'O ficheiro fica guardado no servidor da plataforma, comprimido, e '
    + 'quem tiver acesso a este modelo o poderá abrir e descarregar. O envio, '
    + 'as descargas e uma eventual remoção ficam registados. Só quem carrega, '
    + 'ou um administrador, o pode apagar do servidor.'
  )
}
